import React from 'react';
import PropTypes from 'prop-types';
import { scaleLinear } from 'd3-scale';
import * as dayjs from 'dayjs';
import WeatherIcon from '../../components/WeatherIcons/WeatherIcon';
import { CtoF } from '../../utils/utils';

const BUFFER_TOP = 9;
const BUFFER_BOTTOM = 17;
const CHART_HEIGHT = 300;
const CHART_WIDTH = 1875;
const GRID_WIDTH = 39;
const ICON_SIZE = 41;
const LINE_OPACITY = 0.2;
const WEEKDAY_LABEL_VPOS = CHART_HEIGHT - 35;
const HOUR_LABEL_VPOS = CHART_HEIGHT - 15;
const TEMP_VERT_OFFSET = 55;

const gridLine = (xPos) => (
  <line
    x1={xPos}
    y1={0}
    x2={xPos}
    y2={CHART_HEIGHT}
    stroke="black"
    strokeWidth={0.2}
    opacity={LINE_OPACITY}
  />
);

const TwoDaysChart = ({ forecast }) => {
  let fortyEightHours = forecast.slice(0, 48);

  let lo =
    fortyEightHours.reduce(
      (min, b) => Math.min(min, b.data.instant.details.air_temperature),
      fortyEightHours[0].data.instant.details.air_temperature
    ) - BUFFER_BOTTOM;

  let hi =
    fortyEightHours.reduce(
      (max, b) => Math.max(max, b.data.instant.details.air_temperature),
      fortyEightHours[0].data.instant.details.air_temperature
    ) + BUFFER_TOP;

  let tempPositionScale = scaleLinear()
    .domain([hi, lo])
    .range([0, CHART_HEIGHT]);

  return (
    <>
      <h2 className="weather-hourly-header type-sm type-caps">Hour by Hour</h2>
      <div className="weather-hourly-chart">
        <svg
          className="weather_chartScrollable"
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          xmlns="http://www.w3.org/2000/svg"
        >
          {gridLine(0)}
          {fortyEightHours.map((period, i) => {
            const tem = period.data.instant.details.air_temperature;
            let xPos = i * GRID_WIDTH;
            let yPos = tempPositionScale(tem);
            let date = Date.parse(period.time);
            let hour = dayjs(date).format('ha');
            let dayOfWeek =
              hour === '12AM' ? dayjs(date).format('ddd').toUpperCase() : false;

            return (
              <g key={date} className="weather_chartPoint">
                <WeatherIcon
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  x={xPos}
                  y={yPos}
                  iconUrl={period.data.next_1_hours.summary.symbol_code}
                  fill={
                    period.data.next_1_hours.summary.symbol_code.match(/day/)
                      ? '#fba301'
                      : '#35145a'
                  }
                />
                <g>
                  <text
                    x={`${xPos + GRID_WIDTH / 4}`}
                    y={`${yPos + TEMP_VERT_OFFSET}`}
                  >
                    {CtoF(period.data.instant.details.air_temperature)}
                  </text>
                </g>

                <g>
                  <text
                    className="weather_chartPointHour"
                    x={`${xPos + GRID_WIDTH / 2}`}
                    y={HOUR_LABEL_VPOS}
                    textAnchor="middle"
                  >
                    {hour}
                  </text>
                </g>
                {dayOfWeek && (
                  <g>
                    <text
                      className="weather_chartPointDay"
                      x={xPos + GRID_WIDTH / 2}
                      y={WEEKDAY_LABEL_VPOS}
                      textAnchor="middle"
                    >
                      {dayOfWeek}
                    </text>
                  </g>
                )}
                {gridLine(xPos)}
              </g>
            );
          })}
          {gridLine(CHART_WIDTH)}
        </svg>
      </div>
    </>
  );
};

TwoDaysChart.propTypes = {
  forecast: PropTypes.array,
};

export default TwoDaysChart;
